<script setup lang="ts">
import { useWindowSize } from '@vueuse/core'

definePageMeta({
  layout: 'no-hero',
})

const route = useRoute()
const slug = route.params.slug as string
const { urlFor } = useSanityImage()
const { width } = useWindowSize()

const query = groq`*[_type == "articles" && slug.current == "${slug}"]{
  title,
  heading,
  subtitle,
  body,
  slug,
  image,
  date,
  _id,
  objects,
  updated,
  _createdAt,
  author->,
}[0]`
const sanity = useSanity()
const { data, error } = await useAsyncData(`article-${slug}`, () => sanity.fetch<Sanity.Default.Schema.Articles>(query))
// // console.log(data)

if (error.value) {
  console.error(error)
  throw createError({ status: 404, message: 'Page Not Found' })
}

const useDarkBreadcrumb = ref(false)
onMounted(() => {
  useDarkBreadcrumb.value = width.value < 1024
})

// add some json ld here
useSchemaOrg([
  defineArticle({
    headline: data.value?.title,
    image: data.value?.image ? urlFor(data.value?.image).width(800).url() : '',
    description: data.value?.subtitle,
    url: `https://www.butlersherborn.co.uk/articles/${data.value?.slug?.current}`,
    datePublished: data.value?.date,
    dateModified: data.value?.date,
    author: {
      '@type': 'Person',
      'name': data.value?.author?.name,
      'jobTitle': data.value?.author?.job,
      'image': data.value?.author?.image?.asset?._ref ? urlFor(data.value.author.image.asset).width(800).url() : '',
      'sameAs': [
        `https://www.butlersherborn.co.uk/people/${data.value?.author?.slug.current}`,
      ],
    },
    publisher: {
      '@type': 'Organization',
      'name': 'Butler Sherborn',
      'logo': 'https://www.butlersherborn.co.uk/logo.png',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.butlersherborn.co.uk/articles/${data.value?.slug?.current}`,
    },
  }),
])
</script>

<template>
  <div
    v-if="data"
    class=""
  >
    <Seo
      :title="data.title"
      :description="`${data.title}. ${data.subtitle || ''}`"
      :image-url="data.image ? urlFor(data.image).width(800).height(600).fit('crop').url() : ''"
    />

    <div class="hero relative w-full">
      <div class="absolute inset-0 z-10">
        <div class="grid lg:grid-cols-2 lg:gap-8">
          <div />
          <div class="article-hero-height w-full relative lg:order-2">
            <nuxt-img
              v-if="data.image"
              :src="urlFor(data.image).width(1000).height(1000).fit('crop').url()"
              quality="85"
              :alt="data.image.alt"
              :width="1000"
              :height="1000"
              class="object-cover object-center w-full h-full bg-gray-400"
              sizes="sm:100vw, md:50vw, lg:50vw, xl:50vw"
              loading="lazy"
            />
            <div class="absolute inset-0 bg-black opacity-50 bg-blend-multiply lg:hidden" />
          </div>
        </div>
      </div>

      <div class="container-8xl relative z-20 article-hero-height items-end lg:items-center lg:order-1 grid lg:grid-cols-2 gap-4 lg:gap-8">
        <div class="max-w-3xl mb-4 lg:mb-16">
          <UiBreadcrumb
            :title="data.title"
            class="mb-4"
            :dark="useDarkBreadcrumb"
          />
          <h1 class="text-gray-50 lg:text-gray-900 text-4xl lg:text-6xl font-semibold">
            {{ data.heading }}
          </h1>
          <div v-if="data.date" class="text-gray-100 lg:text-gray-700 lg:text-lg">
            {{ new Date(data.date).toLocaleDateString('en-UK', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }) }}
          </div>
        </div>
      </div>
    </div>

    <div class="lg:-mt-12 pt-4 lg:pt-24 bg-gradient-to-b from-stone-200 to-stone-100 relative z-0 mb-12 overflow-x-hidden">
      <nuxt-img
        src="/android-chrome-512x512.png"
        alt="Butler Sherborn logomark"
        width="400"
        height="400"
        quality="70"
        loading="lazy"
        class="mb-2.5 opacity-5 absolute -top-3 -right-6 z-50"
      />

      <div class="mx-auto container-8xl grid lg:grid-cols-4 lg:gap-12 xl:gap-20">
        <div class="relative mb-4 lg:mb-0">
          <div
            class="author mb-0 lg:mb-12"
            :class="data.objects?.length ? '' : 'lg:sticky lg:top-8'"
          >
            <div class="flex items-center lg:items-start lg:flex-col mb-0 lg:mb-3 gap-4 lg:gap-0">
              <nuxt-link
                v-if="data.author?.slug"
                :to="`/people/${data.author.slug.current}`"
                class="flex items-center"
              >
                <nuxt-img
                  v-if="data.author?.photo"
                  :src="urlFor(data.author.photo).width(100).height(100).url()"
                  quality="90"
                  :alt="data.author.name"
                  width="100"
                  height="100"
                  class="rounded-full object-cover object-center shrink-0 mb-0 lg:mb-3 w-14 lg:w-auto"
                  loading="lazy"
                />
              </nuxt-link>
              <nuxt-img
                v-else-if="data.author?.photo"
                :src="urlFor(data.author.photo).width(100).height(100).url()"
                quality="90"
                :alt="data.author.name"
                width="100"
                height="100"
                class="rounded-full object-cover object-center shrink-0 mb-0 lg:mb-3 w-14 lg:w-auto"
                loading="lazy"
              />
              <div v-if="data.author?.name" class="text-lg font-semibold text-gray-900">
                By {{ data.author.name }}
                <span v-if="data.author?.qualifications" class="text-base text-gray-400">{{ data.author?.qualifications }}</span>
                <p class="mb-0 text-gray-700 text-sm font-normal">
                  {{ data.author?.jobTitle }}
                </p>
              </div>
            </div>
            <UiShare :title="data.title" class="pt-2 hidden lg:flex" />
            <div class="mt-6">
              <p class="text-xs text-gray-500">
                Keep up to date with the latest properties, rural business insights, market commentary and events:
              </p>

              <UiBtn
                size="xs"
                to="https://butlersherbornllp.briefyourmarket.com/NewProfile.aspx"
                label="Join mailing list"
                rel="noopener noreferrer nofollow"
              />
            </div>
          </div>
        </div>
        <div class="main-body max-w-3xl lg:col-span-3 pr-0 lg:pr-12 xl:pr-0">
          <h2 v-if="data.subtitle" class="thin-title">
            {{ data.subtitle }}
          </h2>
          <div v-if="data.body" class="body text-gray-900 pb-12">
            <SanityContentWrapper :blocks="data.body" />
            <div class="absolute -z-10 right-0 top-0 h-full lg:w-4 xl:w-16 2xl:w-24 bg-white" />
          </div>
        </div>
      </div>
      <ObjectsRenderer v-if="data.objects" :objects="data.objects" />
    </div>

    <div class="container-8xl">
      <ArticlesLatest class="mb-12" />

      <LazyCtaImage
        slug="instant-digital-valuation"
        class="pb-12"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.article-hero-height {
  height: calc(100svh - 212px);
  min-height: 550px;
}
</style>
